/**
 * Bulk-drop reconciliation summary for the Film-Print station (BLU-233 quick
 * task 260818-7oj — "10 sent, 8 landed, no error").
 *
 * `handleBulkPrepareAndDrop` / `handleBulkResend` used to report EITHER prepare
 * failures OR per-file drop warnings (mutually exclusive `if/else if`), so one
 * prepare failure silently discarded every drop-failure line. Units skipped
 * because they were already `printing`/`filmPrinted` were invisible too — the
 * operator's card count and the run's `total` could differ with no explanation.
 *
 * `makeOutcomeCollector` gives `prepareAndDropUnitCore` a single place to record
 * ONE outcome per processed unit (dropped / shelf-claimed / failed) plus an
 * optional `#<order> <sku>: <reason>` line. `buildBulkDropSummary` is a PURE
 * function over the resulting counts — unit-tested here without rendering the
 * page (the page closure can't be exercised directly; same rationale as
 * `bulk-tolerant.ts`).
 */

/** One per-unit bucket a bulk run's outcome collector tracks. `dropped` covers
 * a successful drop even when it carries a secondary warning line (e.g. a
 * headerless drop, or a blank-artwork caution) — something DID reach the RIP.
 * `failed` covers everything that did NOT reach the RIP: a real drop failure,
 * no drop payload, or the agent having no DTF hot folder configured at all. */
export type BulkUnitOutcome = 'dropped' | 'shelfClaimed' | 'failed';

export interface BulkOutcomeCollector {
  /** Record ONE outcome for a processed (non-throwing) unit. `line`, when
   * present, is the already-formatted `#<order> <sku>: <reason>` string. */
  record: (outcome: BulkUnitOutcome, line?: string) => void;
  /** Snapshot of counts recorded so far, keyed by outcome. */
  counts: () => { dropped: number; shelfClaimed: number; failed: number };
  /** Every non-empty line recorded so far, in record order. */
  lines: () => string[];
}

/** A fresh collector for one bulk run. */
export function makeOutcomeCollector(): BulkOutcomeCollector {
  const counts = { dropped: 0, shelfClaimed: 0, failed: 0 };
  const lines: string[] = [];
  return {
    record(outcome, line) {
      counts[outcome] += 1;
      if (line) lines.push(line);
    },
    counts: () => ({ ...counts }),
    lines: () => [...lines],
  };
}

export type BulkDropSeverity = 'success' | 'warning' | 'error';

export interface BuildBulkDropSummaryInput {
  /** Total garment cards visible on screen for this batch (the operator's
   * count — what the cards on the page add up to). */
  cardsOnScreen: number;
  /** Units this run actually attempted (already-printing/-done units were
   * excluded upstream — NOT a silent omission; `skipped` below states it). */
  eligible: number;
  /** Units whose drop reached the RIP (may still carry a warning line). */
  dropped: number;
  /** Units whose film was auto-claimed from pre-printed shelf stock (nothing
   * to drop by design — correct behaviour, not a miss). */
  shelfClaimed: number;
  /** Units where nothing reached the RIP: a prepare failure, a drop failure,
   * a missing drop payload, or no DTF agent configured. */
  failed: number;
  /** Every per-file `#<order> <sku>: <reason>` line for this run — prepare
   * failures UNIONED with drop warnings, never one discarding the other. */
  dropWarnings: string[];
  /** Whether the local print agent has a DTF hot folder configured at all. */
  agentHasDtf: boolean;
}

/** Interpolation params for a summary's `bodyKey` — a fixed shape (not
 * `Record<string, number>`) so consumers get `number`, not `number |
 * undefined`, when destructuring. */
export interface BulkDropSummaryBodyParams {
  dropped: number;
  shelfClaimed: number;
  failed: number;
  skipped: number;
  cardsOnScreen: number;
  eligible: number;
  /** The TRUE unsent count for the agent-missing body (`eligible -
   * shelfClaimed`) — a shelf-claimed unit never needed the agent, so it must
   * not be counted among the "none were sent" units. */
  unsent: number;
}

export interface BulkDropSummary {
  severity: BulkDropSeverity;
  /** i18n key for the toast title; empty string on a clean run (no toast). */
  titleKey: string;
  /** i18n key for the toast body; empty string on a clean run (no toast). */
  bodyKey: string;
  /** Interpolation params for `bodyKey` (dropped/shelfClaimed/failed/skipped
   * always present so the copy can state the full reconciliation). */
  bodyParams: BulkDropSummaryBodyParams;
  /** Per-file lines (secondaryMessage block) — union of prepare failures and
   * drop warnings, in run order. */
  lines: string[];
}

/**
 * Build ONE reconciliation summary for a bulk send/resend run.
 * Invariant: `dropped + shelfClaimed + failed + skipped === cardsOnScreen`
 * always holds (skipped = cardsOnScreen - eligible, never negative).
 */
export function buildBulkDropSummary(input: BuildBulkDropSummaryInput): BulkDropSummary {
  const { cardsOnScreen, eligible, dropped, shelfClaimed, failed, dropWarnings, agentHasDtf } = input;
  const skipped = Math.max(0, cardsOnScreen - eligible);
  // Units that still needed the agent and never got sent — a shelf claim
  // never touches the agent by design, so it must never count against it.
  const unsent = Math.max(0, eligible - shelfClaimed);
  const bodyParams = { dropped, shelfClaimed, failed, skipped, cardsOnScreen, eligible, unsent };

  // Loud, unmissable: the agent has no DTF hot folder at all AND at least one
  // unit that actually NEEDED the agent never reached it. Gated on
  // `dropped + shelfClaimed < eligible` (not merely `eligible > 0`) — an
  // all-shelf-claimed run never touches the agent at all, so it must report
  // success, not "agent not detected".
  if (!agentHasDtf && dropped + shelfClaimed < eligible) {
    return {
      severity: 'error',
      titleKey: 'filmPrint.bulkAgentMissingTitle',
      bodyKey: 'filmPrint.bulkAgentMissingBody',
      bodyParams,
      lines: dropWarnings,
    };
  }

  // Fully clean run: nothing failed, no warning lines at all.
  if (failed === 0 && dropWarnings.length === 0) {
    return { severity: 'success', titleKey: '', bodyKey: '', bodyParams, lines: [] };
  }

  return {
    severity: failed > 0 ? 'error' : 'warning',
    titleKey: 'filmPrint.bulkReconcileTitle',
    bodyKey: 'filmPrint.bulkReconcileBody',
    bodyParams,
    lines: dropWarnings,
  };
}
